import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useConfirm } from '../contexts/ConfirmContext';
import { JoinColumnDialog } from '../components/JoinColumnDialog';
import { JobDetailsModal } from '../components/JobDetailsModal';
import { 
  Briefcase, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader, 
  Plus,
  Filter,
  Search,
  Download,
  Eye,
  StopCircle,
  PauseCircle,
  DollarSign,
  FileText,
  Sparkles,
  Save,
  Cloud,
  Key,
  MoreVertical
} from 'lucide-react';
import { promptsAPI, globalPromptsAPI, filesAPI, jobsAPI, authAPI } from '../services/api';
import axios from 'axios';
import { extractApiError } from '../utils/errorMessages';
import { isPreTerminalStatus } from '../utils/jobStatus';
import type {
  PromptTemplate,
  GlobalPromptTemplate,
  FileUpload,
  ProcessingJob,
  ApiKeyStatus,
  CostEstimation,
  StartProcessingRequest,
  ColumnAnalysisResult,
  PaginatedResponse,
  LocationState,
} from '../types';
import { theme } from '../styles/theme';

/** How often a visible list page asks for fresh jobs. Mirrors the detail page's
 *  POLL_INTERVAL_MS; kept local rather than shared because two call sites is a
 *  copy, not an abstraction (CLAUDE.md §8). A third one extracts a hook. */
const JOBS_POLL_INTERVAL_MS = 3000;

/** Ceiling on the exponential back-off after consecutive failures (ADR-048). */
const JOBS_MAX_POLL_BACKOFF_MS = 30_000;

const Jobs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  // Stable per-attempt idempotency key — reset only after a confirmed new job.
  const idempotencyKeyRef = React.useRef<string>(crypto.randomUUID());
  
  // Job list state
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const [loading, setLoading] = useState(false);
  // Refs, not deps: the refresh interval is built once and reads the current
  // list, so it is never torn down and rebuilt on each result.
  const jobsRef = useRef<ProcessingJob[]>([]);
  const inFlightRef = useRef(false);
  const failureCountRef = useRef(0);
  const nextAttemptAtRef = useRef(0);
  jobsRef.current = jobs;
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // API status
  const [apiStatus, setApiStatus] = useState<ApiKeyStatus | null>(null);
  
  // Prompt configuration state
  const [showPromptConfig, setShowPromptConfig] = useState(true);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [globalTemplates, setGlobalTemplates] = useState<GlobalPromptTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | GlobalPromptTemplate | null>(null);
  const [prompt, setPrompt] = useState('');
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  
  // Project and job naming
  const [projectName, setProjectName] = useState('');
  
  // Processing configuration state
  const [modelName, setModelName] = useState('gemini-2.5-flash');
  const [temperature, setTemperature] = useState(0.1);
  const [chunkSize, setChunkSize] = useState(20);
  const [maxConcurrent, setMaxConcurrent] = useState(5);
  
  // Cost estimation state
  const [outputMultiplier, setOutputMultiplier] = useState(1.8);
  const [estimation, setEstimation] = useState<CostEstimation | null>(null);
  const [estimating, setEstimating] = useState(false);
  
  // Change detection state - track what was estimated
  const [estimatedConfig, setEstimatedConfig] = useState<StartProcessingRequest | null>(null);
  
  // File selection state
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileUpload | null>(null);
  
  // Join column analysis state
  const [columnAnalysis, setColumnAnalysis] = useState<ColumnAnalysisResult | null>(null);
  const [analyzingColumns, setAnalyzingColumns] = useState(false);
  const [selectedJoinColumn, setSelectedJoinColumn] = useState<string | null>(null);
  const [showJoinColumnDialog, setShowJoinColumnDialog] = useState(false);
  
  // Job details modal state
  const [selectedJobForDetails, setSelectedJobForDetails] = useState<ProcessingJob | null>(null);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);

  // Load initial data
  useEffect(() => {
    loadJobs();
    loadTemplates();
    loadGlobalTemplates();
    loadFiles();
    loadApiStatus();
    
    // Check if we're coming from Upload page with a file
    const state = location.state as LocationState | null;
    if (state?.fileId) {
      filesAPI.get(state.fileId).then(response => {
        setSelectedFile(response.data);
        setShowPromptConfig(true);
      });
    }
  }, [location]);
  
  // Load API status
  const loadApiStatus = async () => {
    try {
      const response = await authAPI.getApiKeyStatus();
      setApiStatus(response.data);
    } catch (error) {
      console.error('Failed to load API status:', error);
    }
  };

  // Auto-refresh jobs to show progress (silent refresh - no loading state).
  //
  // Deps are [] and the job list is read from a ref. They used to be [jobs],
  // which tore the interval down and rebuilt it on every refresh result,
  // because loadJobs sets a fresh array each time.
  //
  // A hidden tab refreshes nothing; returning to one refreshes immediately.
  // Same reasoning as the detail page (ADR-048) — a backgrounded list page was
  // issuing 20 requests a minute for as long as it stayed open.
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      nextAttemptAtRef.current = 0;
      loadJobs(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    const interval = setInterval(() => {
      if (document.hidden) return;
      if (Date.now() < nextAttemptAtRef.current) return;
      // isPreTerminalStatus is the single source of truth for poll-or-stop
      // (frontend/src/utils/jobStatus.ts, which forbids inlining the list at
      // call sites). This was an inlined ['processing','queued'] literal that
      // omitted `pending`, so a job in the reconcile window looked frozen here
      // even after M7 fixed the detail page for exactly that status.
      if (jobsRef.current.some(job => isPreTerminalStatus(job.status))) {
        loadJobs(true); // Pass true for silent refresh
      }
    }, JOBS_POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  
  // Watch for changes in configuration and invalidate estimation if needed
  // This ensures user sees the warning banner when they change settings
  useEffect(() => {
    // Don't invalidate on initial load or if no estimation exists yet
    if (!estimation || !estimatedConfig) return;
    
    // Config has changed - the warning banner will show automatically via hasConfigChanged()
    // No need to clear the estimation, just let the banner appear
  }, [selectedFile?.id, prompt, modelName, outputMultiplier, estimation, estimatedConfig]);

  // Load user prompt templates
  const loadTemplates = async () => {
    try {
      const response = await promptsAPI.list();
      // Handle both array and paginated response formats
      const templateData: PromptTemplate[] = Array.isArray(response.data) 
        ? response.data 
        : (response.data as PaginatedResponse<PromptTemplate>).results || [];
      setTemplates(templateData);
      
      // Set default template if available
      const defaultTemplate = templateData.find((t) => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate);
        setPrompt(defaultTemplate.prompt_text);
      }
    } catch (error: unknown) {
      console.error('Failed to load templates:', error);
      setTemplates([]); // Set empty array on error
      // Only show alert for non-404 errors (404 means no templates yet)
      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        console.warn('Could not load templates. You can still create jobs without templates.');
      }
    }
  };

  // Load global prompt templates
  const loadGlobalTemplates = async () => {
    try {
      const response = await globalPromptsAPI.list();
      const templateData: GlobalPromptTemplate[] = Array.isArray(response.data) 
        ? response.data 
        : (response.data as PaginatedResponse<GlobalPromptTemplate>).results || [];
      setGlobalTemplates(templateData);
    } catch (error) {
      console.error('Failed to load global templates:', error);
      setGlobalTemplates([]);
    }
  };

  // Load files
  const loadFiles = async () => {
    try {
      const response = await filesAPI.list();
      // Handle both array and paginated response formats
      const fileData: FileUpload[] = Array.isArray(response.data)
        ? response.data
        : (response.data as PaginatedResponse<FileUpload>).results || [];
      setFiles(fileData);
    } catch (error) {
      console.error('Failed to load files:', error);
      setFiles([]); // Set empty array on error
    }
  };

  // Load jobs
  const loadJobs = async (silent = false) => {
    // One request at a time — ticks must not stack up behind a slow response
    // (ADR-046 has the arithmetic on what that did to the server).
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const response = await jobsAPI.list();
      // Handle both array and paginated response formats
      const jobData = Array.isArray(response.data) ? response.data : ((response.data as any).results || []);
      setJobs(jobData);
      nextAttemptAtRef.current = 0;
      failureCountRef.current = 0;
    } catch (error) {
      console.error('Failed to load jobs:', error);
      // Deliberately does NOT clear the list. It used to `setJobs([])`, so one
      // failed background refresh showed "no jobs" to a user who had jobs —
      // the list-page form of the latched "Job Not Found" (PDR-047). A failed
      // refresh says nothing about whether the jobs exist, so keep showing the
      // last good list and try again, more slowly each time.
      failureCountRef.current += 1;
      nextAttemptAtRef.current = Date.now() + Math.min(
        JOBS_MAX_POLL_BACKOFF_MS,
        JOBS_POLL_INTERVAL_MS * 2 ** (failureCountRef.current - 1),
      );
    } finally {
      inFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  };

  // Estimate tokens and cost
  // Save prompt template
  const handleSaveTemplate = async () => {
    if (!promptName || !prompt) return;
    
    try {
      await promptsAPI.create({
        name: promptName,
        description: promptDescription,
        prompt_text: prompt,
      });
      
      await loadTemplates();
      setShowSaveTemplate(false);
      setPromptName('');
      setPromptDescription('');
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't save that template"), variant: 'error' });
    }
  };

    // Select template (handles both user and global templates)
  const handleSelectTemplate = (template: PromptTemplate | GlobalPromptTemplate) => {
    setSelectedTemplate(template);
    setPrompt(template.prompt_text);
  };

  // Check if configuration has changed since last estimation
  const hasConfigChanged = (): boolean => {
    if (!estimatedConfig) return false;
    
    return (
      estimatedConfig.file_id !== selectedFile?.id ||
      estimatedConfig.system_instructions !== prompt ||
      estimatedConfig.model_name !== modelName ||
      estimatedConfig.output_multiplier !== outputMultiplier
    );
  };
  
  // Analyze columns for join column suggestion
  const handleAnalyzeColumns = async () => {
    if (!selectedFile) {
      toast({ title: 'Choose a file first', variant: 'warning' });
      return;
    }
    
    setAnalyzingColumns(true);
    try {
      const response = await jobsAPI.analyzeColumns({
        file_id: selectedFile.id as string,
      });
      
      setColumnAnalysis(response.data);
      setSelectedJoinColumn(response.data.suggested_join_column);
      setShowJoinColumnDialog(true);
    } catch (error: unknown) {
      console.error('Failed to analyze columns:', error);
      toast({ title: "Couldn't read the columns in that file", description: 'Check the file opens correctly, then try again.', variant: 'error' });
    } finally {
      setAnalyzingColumns(false);
    }
  };
  
  // Estimate cost using Gemini token counter
  const handleEstimateForJob = async () => {
    if (!selectedFile || !prompt) {
      toast({ title: 'Choose a file and write a prompt first', variant: 'warning' });
      return;
    }
    
    setEstimating(true);
    try {
      const response = await jobsAPI.estimate({
        file_id: selectedFile.id,
        prompt_text: prompt,
        model_name: modelName,
        temperature: temperature,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        output_multiplier: outputMultiplier,
      });
      
      setEstimation(response.data);
      
      // Save current config as the estimated config
      setEstimatedConfig({
        file_id: selectedFile.id,
        system_instructions: prompt,
        model_name: modelName,
        output_multiplier: outputMultiplier,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
      });
    } catch (error: unknown) {
      console.error('Failed to estimate:', error);
      
      // Check if it's an API credentials error
      if (axios.isAxiosError(error) && error.response?.data?.error === 'API_CREDENTIALS_NOT_CONFIGURED') {
        toast({
          title: 'API Credentials Not Configured',
          description: (error.response.data as Record<string, unknown>).detail as string || 'Please configure your Gemini or Vertex AI credentials.',
          variant: 'error',
          duration: 5000,
        });
        navigate('/profile?tab=api');
      } else {
        toast({ title: "Couldn't estimate the cost", description: 'Try again in a moment.', variant: 'error' });
      }
    } finally {
      setEstimating(false);
    }
  };

  // Start job with estimation
  const handleStartJob = async () => {
    console.log('handleStartJob called');
    console.log('  selectedFile:', selectedFile);
    console.log('  prompt:', prompt);
    console.log('  estimation:', estimation);
    console.log('  hasConfigChanged:', hasConfigChanged());
    
    if (!selectedFile || !prompt) {
      console.log(' Missing file or prompt');
      toast({ title: 'Choose a file and write a prompt first', variant: 'warning' });
      return;
    }
    
    // Require estimation first
    if (!estimation || !estimation.estimated_cost) {
      console.log(' Missing estimation');
      toast({ title: 'Estimate the cost before starting', variant: 'warning' });
      return;
    }
    
    // Check if configuration has changed since estimation
    if (hasConfigChanged()) {
      console.log(' Configuration has changed since estimation');
      toast({
        title: 'Your settings changed since the last estimate',
        description: 'Re-estimate the cost before starting this run.',
        variant: 'warning',
      });
      return;
    }
    
    // Step 1: Analyze columns and show join column dialog
    if (!columnAnalysis || !selectedJoinColumn) {
      console.log('📊 Analyzing columns for join column suggestion...');
      await handleAnalyzeColumns();
      return; // Dialog will be shown, user will confirm from there
    }
    
    // Step 2: If we have join column, proceed with job start
    console.log(' Join column confirmed:', selectedJoinColumn);
    await proceedWithJobStart();
  };
  
  // Proceed with job start after join column is confirmed
  const proceedWithJobStart = async () => {
    if (!selectedFile || !prompt || !estimation) return;
    
    // Structured rather than a newline-joined string. The paid branch used
    // `\\n` inside a template literal, so the dialog printed the characters
    // "\n" instead of breaking the line \u2014 only on the branch that actually
    // costs money, and invisible to tests because a native confirm is.
    const rowLabel = (estimation.row_count || 0).toLocaleString();
    const modelLabel = estimation.model_name || 'the selected model';
    const costMessage = estimation.is_free
      ? {
          title: 'Start this run?',
          description: `${rowLabel} rows with ${modelLabel}. No cost on the free tier.`,
          confirmText: 'Start run',
        }
      : {
          title: 'Start this run?',
          description:
            `${rowLabel} rows with ${modelLabel}. Estimated cost ` +
            `$${(estimation.estimated_cost?.usd || 0).toFixed(4)} USD ` +
            `(S$${(estimation.estimated_cost?.sgd || 0).toFixed(4)}).`,
          confirmText: 'Start run',
        };
    
    console.log('📋 Showing confirmation dialog');
    if (!(await confirm(costMessage))) {
      console.log(' User cancelled');
      return;
    }
    
    console.log(' User confirmed, starting job...');
    
    try {
      console.log('📤 Sending job start request with:', {
        file_id: selectedFile.id,
        prompt_text: prompt.substring(0, 50) + '...',
        model_name: modelName,
        temperature: temperature,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
      });
      
      const response = await jobsAPI.start({
        file_id: selectedFile.id,
        project_name: projectName || undefined,
        prompt_text: prompt,
        prompt_template_id: selectedTemplate?.id,
        model_name: modelName,
        temperature: temperature,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        join_column: selectedJoinColumn || undefined,
      }, idempotencyKeyRef.current);
      idempotencyKeyRef.current = crypto.randomUUID();
      
      if (response.data.is_replay) {
        toast({
          title: 'Job already submitted',
          description: 'This job was already submitted — showing your existing job.',
          variant: 'warning',
          duration: 5000,
        });
      }
      
      console.log(' Job started successfully:', response.data);
      
      await loadJobs(true);
      setShowPromptConfig(false);
      setEstimation(null); // Clear estimation
      setEstimatedConfig(null); // Clear estimated config
      setColumnAnalysis(null); // Clear column analysis
      setSelectedJoinColumn(null); // Clear join column selection
      
      // Increment usage count for template (only for user templates, not global)
      if (selectedTemplate && 'is_favorite' in selectedTemplate) {
        try {
          await promptsAPI.get(selectedTemplate.id);
        } catch (err) {
          // Ignore if template fetch fails
          console.warn('Could not update template usage count:', err);
        }
      }
    } catch (error: unknown) {
      console.error(' Failed to start job:', error);
      
      // Check if it's an API credentials error
      if (axios.isAxiosError(error) && error.response?.data?.error === 'API_CREDENTIALS_NOT_CONFIGURED') {
        toast({
          title: 'API Credentials Not Configured',
          description: (error.response.data as Record<string, unknown>).detail as string || 'Please configure your Gemini or Vertex AI credentials.',
          variant: 'error',
          duration: 5000,
        });
        navigate('/profile?tab=api');
        return;
      }
      
      toast({ title: extractApiError(error, "Couldn't start that run"), variant: 'error' });
    }
  };

  const handleCancelJob = async (jobId: number | string) => {
    console.log('🛑 handleCancelJob called with:', jobId, 'type:', typeof jobId);
    
    const okCancel = await confirm({
      title: 'Cancel this run?',
      description: 'Work already done is kept, but the run cannot be restarted from here.',
      confirmText: 'Cancel run',
      cancelText: 'Keep running',
      destructive: true,
    });
    if (!okCancel) {
      return;
    }

    try {
      // Job ID is a UUID string, not a number - don't try to parse it
      if (!jobId) {
        console.error(' Invalid job ID:', jobId);
        toast({ title: "Something went wrong with that run", description: 'Reload the page and try again.', variant: 'error' });
        return;
      }
      
      console.log('🛑 Cancelling job:', jobId);
      await jobsAPI.cancel(jobId);
      console.log(' Job cancelled successfully');
      
      // Immediately update the local state to show cancelled status
      setJobs(prevJobs => prevJobs.map(job => 
        job.id === jobId 
          ? { ...job, status: 'cancelled' as const }
          : job
      ));
      
      // Reload jobs to get fresh data
      await loadJobs(true);
    } catch (error: unknown) {
      console.error(' Failed to cancel job:', error);
      toast({ title: extractApiError(error, "Couldn't cancel that run"), variant: 'error' });
    }
  };

  const handleResumeJob = async (jobId: number | string) => {
    console.log('handleResumeJob called with:', jobId);
    
    const okResume = await confirm({
      title: 'Resume this run?',
      description: 'It carries on from its last checkpoint, so finished work is not repeated.',
      confirmText: 'Resume',
    });
    if (!okResume) {
      return;
    }

    try {
      if (!jobId) {
        console.error(' Invalid job ID:', jobId);
        toast({ title: "Something went wrong with that run", description: 'Reload the page and try again.', variant: 'error' });
        return;
      }
      
      console.log('Resuming job:', jobId);
      const result = await jobsAPI.resume(jobId);
      const resumeData = result.data as unknown as import('../types').ResumeJobResponse;

      // Standard resume: { message, new_job: { id, ... }, ... }  → new job nested under 'new_job'
      // Dimension resume: { message, id, ... }  → same job spread at root level
      const newJobId: string | undefined =
        resumeData.new_job?.id != null ? String(resumeData.new_job.id)
        : resumeData.id != null ? String(resumeData.id) : undefined;

      console.log(' Job resumed, redirecting to:', newJobId);
      navigate(`/jobs/${newJobId}`);
      
    } catch (error: unknown) {
      console.error(' Failed to resume job:', error);
      toast({ title: extractApiError(error, "Couldn't resume that run"), variant: 'error' });
    }
  };

  const handleDownloadResults = async (job: ProcessingJob) => {
    try {
      // Part-category jobs export to XLSX via the export-validation/ endpoint
      if (job.pipeline_type === 'part_category') {
        const response = await jobsAPI.exportValidation(job.id);
        const filename = `part_category_export_${String(job.id).slice(0, 8)}.xlsx`;
        const url = window.URL.createObjectURL(response.data as Blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }

      console.log('📥 Downloading results for job:', job.id);

      const response = await jobsAPI.download(job.id);
      const blobData = response.data as Blob;

      // Try to parse as JSON first (Tigris pre-signed URL path returns JSON)
      let parsedJson: Record<string, unknown> | null = null;
      try {
        const text = await blobData.text();
        parsedJson = JSON.parse(text) as Record<string, unknown>;
      } catch {
        // Not JSON — it's a direct binary file (local dev / old DB method)
      }

      console.log('📦 Response received:', {
        blobSize: blobData.size,
        blobType: blobData.type,
        hasDownloadUrl: parsedJson !== null && 'download_url' in parsedJson,
      });

      if (parsedJson && 'download_url' in parsedJson) {
        // Direct download from Tigris using pre-signed URL
        const downloadUrl = parsedJson.download_url as string;
        const filename = parsedJson.filename as string | undefined;
        console.log(' Using Tigris pre-signed URL for download');

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename || job.output_filename || `${job.filename}_processed.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Direct binary download (local dev or old database method)
        console.log('Using direct blob download');

        const contentDisposition = (response.headers?.['content-disposition'] as string) ?? '';
        const filenameMatch = /filename="([^"]+)"/.exec(contentDisposition);
        const downloadFilename =
          filenameMatch?.[1] ||
          job.output_filename ||
          `dimension_export_${String(job.id).slice(0, 8)}.zip`;

        console.log('💾 Downloading as:', downloadFilename);

        const url = window.URL.createObjectURL(blobData);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      console.log(' Download completed');
    } catch (error: unknown) {
      console.error(' Failed to download results:', error);
      toast({ title: extractApiError(error, "Couldn't download the results"), variant: 'error' });
    }
  };

  const handleDownloadLog = async (job: ProcessingJob) => {
    try {
      console.log('📄 Downloading log for job:', job.id);
      
      const response = await jobsAPI.download(job.id);
      
      // Create blob for log file
      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data], { type: 'text/plain;charset=utf-8;' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate log filename
      const projectPart = job.project_name ? `${job.project_name}_` : '';
      const jobPart = job.job_name || job.id;
      const filename = `job_log_${projectPart}${jobPart}.txt`.replace(/ /g, '_');
      link.download = filename;
      
      console.log('💾 Downloading log as:', filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(' Log download completed');
    } catch (error: unknown) {
      console.error(' Failed to download log:', error);
      toast({ title: extractApiError(error, 'No log available for this run yet'), variant: 'error' });
    }
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = 
      job.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (job.job_name?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-3xl font-bold ${theme.text.primary} flex items-center`}>
            <Briefcase className="w-8 h-8 mr-3" />
            Jobs
          </h2>
          
          <Link to="/upload" className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold rounded-lg transition duration-200 flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Job</span>
          </Link>
        </div>
        
      </div>

      <div className={`${theme.card.background} rounded-lg shadow-lg p-6 mb-6`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary w-5 h-5" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-text-tertiary" />
            <button onClick={() => setStatusFilter('all')} className={`px-4 py-2 rounded-lg ${statusFilter === 'all' ? 'bg-interactive-primary text-text-inverse' : 'bg-bg-subtle'}`}>All</button>
            <button onClick={() => setStatusFilter('completed')} className={`px-4 py-2 rounded-lg ${statusFilter === 'completed' ? 'bg-interactive-primary text-text-inverse' : 'bg-bg-subtle'}`}>Completed</button>
            <button onClick={() => setStatusFilter('processing')} className={`px-4 py-2 rounded-lg ${statusFilter === 'processing' ? 'bg-interactive-primary text-text-inverse' : 'bg-bg-subtle'}`}>Processing</button>
            <button onClick={() => setStatusFilter('cancelled')} className={`px-4 py-2 rounded-lg ${statusFilter === 'cancelled' ? 'bg-interactive-primary text-text-inverse' : 'bg-bg-subtle'}`}>Cancelled</button>
            <button onClick={() => setStatusFilter('suspended')} className={`px-4 py-2 rounded-lg ${statusFilter === 'suspended' ? 'bg-qc-warning text-text-inverse' : 'bg-bg-subtle'}`} data-testid="filter-suspended">Paused</button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className={`${theme.card.background} rounded-lg shadow-lg p-12 text-center`}>
          <Loader className="w-12 h-12 text-interactive-primary animate-spin mx-auto mb-4" />
          <p className={`${theme.text.secondary}`}>Loading jobs...</p>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className={`${theme.card.background} rounded-lg shadow-lg p-12 text-center`}>
          <Briefcase className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
          <h3 className={`text-xl font-semibold ${theme.text.primary} mb-2`}>No jobs found</h3>
          <p className={`${theme.text.secondary} mb-6`}>
            {jobs.length === 0 ? 'Upload a file to start your first processing job' : 'No jobs match your search criteria'}
          </p>
          <Link to="/upload" className="inline-flex items-center space-x-2 px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold rounded-lg">
            <Plus className="w-5 h-5" />
            <span>Create New Job</span>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredJobs.map((job) => (
            <div key={job.id} className={`${theme.card.background} rounded-lg shadow p-4 hover:shadow-lg transition-shadow`}>
              {/* Header Row - Compact */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center space-x-3">
                    <h3 className={`text-base font-semibold ${theme.text.primary} truncate`}>
                      {job.job_name || job.filename}
                    </h3>
                    {/* Status Badge - Inline */}
                    {job.status === 'completed' && (
                      <span className="px-2 py-0.5 bg-qc-success/10 text-qc-success rounded text-xs font-medium flex items-center whitespace-nowrap">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Completed
                      </span>
                    )}
                    {job.status === 'processing' && (
                      <span className="px-2 py-0.5 bg-interactive-primary/10 text-interactive-primary rounded text-xs font-medium flex items-center whitespace-nowrap">
                        <Loader className="w-3 h-3 mr-1 animate-spin" />
                        Processing
                      </span>
                    )}
                    {job.status === 'failed' && (
                      <span className="px-2 py-0.5 bg-qc-error/10 text-qc-error rounded text-xs font-medium flex items-center whitespace-nowrap">
                        <XCircle className="w-3 h-3 mr-1" />
                        Failed
                      </span>
                    )}
                    {job.status === 'pending' && (
                      <span className="px-2 py-0.5 bg-qc-warning/10 text-qc-warning rounded text-xs font-medium flex items-center whitespace-nowrap">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </span>
                    )}
                    {job.status === 'queued' && (
                      <span className="px-2 py-0.5 bg-bg-subtle text-text-secondary rounded text-xs font-medium flex items-center whitespace-nowrap">
                        <Clock className="w-3 h-3 mr-1" />
                        Queued
                      </span>
                    )}
                    {job.status === 'cancelled' && (
                      <span className={`px-2 py-0.5 ${theme.background.tertiary} text-text-primary rounded text-xs font-medium flex items-center whitespace-nowrap`}>
                        <StopCircle className="w-3 h-3 mr-1" />
                        Cancelled
                      </span>
                    )}
                    {job.status === 'suspended' && (
                      <span className="px-2 py-0.5 bg-qc-warning/10 text-qc-warning rounded text-xs font-medium flex items-center whitespace-nowrap" data-testid="badge-suspended">
                        <PauseCircle className="w-3 h-3 mr-1" />
                        Paused
                      </span>
                    )}
                  </div>
                  {job.job_name && (
                    <p className={`text-xs ${theme.text.secondary} truncate mt-0.5`}>
                      {job.filename}
                    </p>
                  )}
                  {job.organization_name && (
                    <p className={`text-xs ${theme.text.secondary} truncate mt-0.5`}>
                      📁 {job.organization_name}
                    </p>
                  )}
                </div>

                {/* Action Buttons - Compact */}
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => {
                      navigate(`/jobs/${job.id}`);
                    }}
                    className={`px-3 py-1.5 ${theme.background.tertiary} text-text-secondary rounded hover:bg-bg-hover flex items-center space-x-1 text-sm transition-colors`}
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="hidden sm:inline">Details</span>
                  </button>
                  {(job.status === 'completed' || job.status === 'completed_with_errors') && (
                    <>
                      <button 
                        onClick={() => handleDownloadResults(job)}
                        className="px-3 py-1.5 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded flex items-center space-x-1 text-sm transition-colors"
                        title="Download Results"
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Download</span>
                      </button>
                      <button 
                        onClick={() => handleDownloadLog(job)}
                        className="px-3 py-1.5 bg-bg-subtle hover:bg-bg-hover border border-border-strong text-text-primary rounded flex items-center space-x-1 text-sm transition-colors"
                        title="Download Server Log"
                      >
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline">Log</span>
                      </button>
                    </>
                  )}
                  {/* JM-51: driven by the server's can_cancel, not a status list of
                      our own. The list here read ['processing','queued','pending'] while
                      the endpoint allowed four more — so a suspended job that could not
                      be resumed showed no exit at all, which is the JM-43 dead end
                      PDR-032 closed on the detail page and not here. */}
                  {(job.can_cancel ?? ['processing', 'queued', 'pending'].includes(job.status)) && (
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('Cancel button clicked for job:', job.id, job);
                        handleCancelJob(job.id);
                      }}
                      className="px-3 py-1.5 bg-qc-error hover:bg-qc-error/90 text-text-inverse rounded flex items-center space-x-1 text-sm transition-colors"
                      title="Cancel Job"
                    >
                      <StopCircle className="w-4 h-4" />
                      <span className="hidden sm:inline">Cancel</span>
                    </button>
                  )}
                  {(job.status === 'failed' || job.status === 'cancelled' || job.status === 'suspended') && (
                    <>
                      {job.can_resume && (
                        <button 
                          onClick={() => handleResumeJob(job.id)}
                          className="px-3 py-1.5 bg-qc-success hover:bg-qc-success/90 text-text-inverse rounded flex items-center space-x-1 text-sm transition-colors"
                          title="Resume from Checkpoint"
                          data-testid="resume-job-btn"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="hidden sm:inline">Resume</span>
                        </button>
                      )}
                      <button 
                        onClick={() => handleDownloadLog(job)}
                        className="px-3 py-1.5 bg-bg-subtle hover:bg-bg-hover border border-border-strong text-text-primary rounded flex items-center space-x-1 text-sm transition-colors"
                        title="Download Server Log"
                      >
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline">Log</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Progress Bar - Only for Active Jobs */}
              {(job.status === 'processing' || job.status === 'queued') && (
                <div className="mb-3">
                  <div className={`flex justify-between text-xs ${theme.text.secondary} mb-1`}>
                    <span>{job.progress.toFixed(1)}%</span>
                    <span>{job.processed_rows || 0} / {job.total_rows || 0} rows</span>
                  </div>
                  <div className="w-full bg-bg-hover rounded-full h-1.5">
                    <div
                      className="bg-interactive-primary h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${job.progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Stats Row - Horizontal, Compact */}
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3 text-text-tertiary" />
                    <span className={theme.text.secondary}>{new Date(job.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FileText className="w-3 h-3 text-text-tertiary" />
                    <span className={theme.text.secondary}>{(job.total_rows || 0).toLocaleString()} rows</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-3 h-3 text-text-tertiary" />
                    <span className={theme.text.secondary}>${(parseFloat(job.cost_sgd as any) || 0).toFixed(4)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Sparkles className="w-3 h-3 text-text-tertiary" />
                    <span className={theme.text.secondary}>{(job.total_tokens || 0).toLocaleString()} tokens</span>
                  </div>
                </div>
              </div>

              {/* Error Message - Compact */}
              {job.error_message && (
                <div className={`mt-3 p-2 ${theme.status.error.background} border border-qc-error/30 rounded`}>
                  <p className="text-xs text-qc-error line-clamp-2">
                    <strong>Error:</strong> {job.error_message}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Join Column Selection Dialog */}
      <JoinColumnDialog
        open={showJoinColumnDialog}
        columnAnalysis={columnAnalysis}
        selectedColumn={selectedJoinColumn}
        onSelectColumn={setSelectedJoinColumn}
        onConfirm={proceedWithJobStart}
        onCancel={() => {
          setShowJoinColumnDialog(false);
          setColumnAnalysis(null);
          setSelectedJoinColumn(null);
        }}
      />

      {/* Job Details Modal */}
      <JobDetailsModal
        job={selectedJobForDetails}
        open={showJobDetailsModal}
        onClose={() => {
          setShowJobDetailsModal(false);
          setSelectedJobForDetails(null);
        }}
      />
    </div>
  );
};

export default Jobs;
